import React from 'react';
import { Home, Plus, CheckCircle, Trees, Eye, EyeOff, AlertTriangle } from 'lucide-react';
import { ProposalNodeSet } from '../../types';
import { formatStructureTimberSpecSummary } from '../../utils/proposalTimberDefaults';
import { structureIsExclusive } from '../../utils/proposalExclusiveGroups';
import './StructureGridCard.css';

interface StructureGridCardProps {
    nodeSet: ProposalNodeSet;
    onClick: () => void;
    hidePrice?: boolean;
    hideInstall?: boolean;
    kitMode?: boolean;
    availableSpecies?: string[];
    selectedSpecies?: string[];
    onSpeciesToggle?: (species: string) => void;
    onToggleEnabled?: (id: string) => void;
    onToggleClientOptional?: (id: string) => void;
    canMarkOptional?: boolean;
    onToggleClientExclusive?: (id: string) => void;
    /** Phased projects: selectable phases for this structure (undefined = phasing off). */
    phaseOptions?: Array<{ id: string; name: string; committed?: boolean }>;
    /** Effective phase id for this structure (resolved by the parent). */
    phaseId?: string | null;
    onPhaseChange?: (nodeSetId: string, phaseId: string) => void;
    /** Structure is in a committed phase — assignment locked. */
    phaseLocked?: boolean;
    disabled?: boolean;
    timberListHasIssues?: boolean;
    timberOrderLengthUnder8Min?: boolean;
    /** Share of enabled structures' raw total (0–100). */
    projectSharePercent?: number | null;
    /** Freight and overhead are global — skip them when judging whether a structure is filled in. */
    usesGlobalStructureCosting?: boolean;
}

const isStructureComplete = (
    nodeSet: ProposalNodeSet,
    usesGlobalStructureCosting = true,
): boolean => {
    const categories = [
        nodeSet.design.total,
        nodeSet.production.total,
        nodeSet.materials.total,
        ...(usesGlobalStructureCosting
            ? []
            : [nodeSet.freight.total, nodeSet.overhead.total]),
        nodeSet.install.total,
        nodeSet.travel.total,
    ];
    const filledCount = categories.filter(t => t > 0).length;
    return filledCount >= 3;
};

const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(value);
};

const StructureGridCard: React.FC<StructureGridCardProps> = ({
    nodeSet,
    onClick,
    hidePrice = false,
    hideInstall = false,
    kitMode = false,
    availableSpecies = [],
    selectedSpecies = [],
    onSpeciesToggle,
    onToggleEnabled,
    onToggleClientOptional,
    canMarkOptional = true,
    onToggleClientExclusive,
    phaseOptions,
    phaseId = null,
    onPhaseChange,
    phaseLocked = false,
    disabled = false,
    timberListHasIssues = false,
    timberOrderLengthUnder8Min = false,
    projectSharePercent = null,
    usesGlobalStructureCosting = true,
}) => {
    const completed = isStructureComplete(nodeSet, usesGlobalStructureCosting);
    const isEnabled = nodeSet.enabled !== false;
    const quantity = nodeSet.quantity || 1;
    const unitTotal = hideInstall
        ? nodeSet.design.total +
        nodeSet.production.total +
        nodeSet.materials.total +
        nodeSet.freight.total +
        nodeSet.overhead.total
        : nodeSet.design.total +
        nodeSet.production.total +
        nodeSet.materials.total +
        nodeSet.freight.total +
        nodeSet.overhead.total +
        nodeSet.install.total +
        nodeSet.travel.total;
    const total = unitTotal * quantity;
    const hasImage = nodeSet.images && nodeSet.images.length > 0;
    const timberSpecSummary = formatStructureTimberSpecSummary(nodeSet.materials);

    const handleSpeciesClick = (e: React.MouseEvent, species: string) => {
        e.stopPropagation();
        if (onSpeciesToggle && !disabled) {
            onSpeciesToggle(species);
        }
    };

    const handleToggleEnabled = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (onToggleEnabled && !disabled) {
            onToggleEnabled(nodeSet.id);
        }
    };

    const isClientOptional = nodeSet.clientOptional === true;
    const isClientExclusive = structureIsExclusive(nodeSet);

    return (
        <div
            className={`structure-grid-card ${completed ? 'completed' : ''} ${kitMode ? 'kit-mode' : ''} ${disabled ? 'locked' : ''} ${!isEnabled ? 'structure-disabled' : ''}`}
            onClick={() => onClick()}
            style={{ position: 'relative' }}
        >
            {((onToggleEnabled && !disabled) || onToggleClientOptional || onToggleClientExclusive) && (
                <div className="structure-card-controls" onClick={(e) => e.stopPropagation()}>
                    {onToggleEnabled && !disabled && (
                        <button
                            className={`structure-toggle-btn ${isEnabled ? 'enabled' : 'disabled'}`}
                            onClick={handleToggleEnabled}
                            title={isEnabled ? 'Exclude from proposal' : 'Include in proposal'}
                        >
                            {isEnabled ? <Eye size={14} /> : <EyeOff size={14} />}
                        </button>
                    )}
                    <div className="structure-card-flag-toggles">
                    {onToggleClientOptional && isEnabled && (
                        <label
                            className={`structure-optional-toggle ${isClientOptional ? 'active' : ''} ${!canMarkOptional && !isClientOptional ? 'blocked' : ''}`}
                            title={
                                !canMarkOptional && !isClientOptional
                                    ? 'At least one structure must remain required'
                                    : isClientExclusive
                                        ? 'Turn off Choose one first to make this an independent add-on'
                                        : 'Allow client to opt out of this structure on the public proposal'
                            }
                        >
                            <input
                                type="checkbox"
                                checked={isClientOptional}
                                onChange={() => onToggleClientOptional(nodeSet.id)}
                                disabled={!canMarkOptional && !isClientOptional}
                            />
                            <span className="structure-optional-slider" aria-hidden />
                            <span className="structure-optional-label">Client optional</span>
                        </label>
                    )}
                    {onToggleClientExclusive && isEnabled && (
                        <label
                            className={`structure-optional-toggle structure-exclusive-toggle ${isClientExclusive ? 'active' : ''}`}
                            title="Client can purchase only one of the structures with this turned on"
                        >
                            <input
                                type="checkbox"
                                checked={isClientExclusive}
                                onChange={() => onToggleClientExclusive(nodeSet.id)}
                                disabled={disabled}
                            />
                            <span className="structure-optional-slider" aria-hidden />
                            <span className="structure-optional-label">Choose one</span>
                        </label>
                    )}
                    </div>
                </div>
            )}
            <div className="structure-card-icon">
                {hasImage ? (
                    <img
                        src={nodeSet.images![0].url}
                        alt={nodeSet.name}
                        className="structure-thumbnail"
                    />
                ) : (
                    <Home />
                )}
            </div>
            {(quantity > 1 ||
                (isEnabled && nodeSet.clientOptional) ||
                (isEnabled && isClientExclusive) ||
                (isEnabled && nodeSet.excludeFromGlobalPrices) ||
                (isEnabled && nodeSet.excludeInstall) ||
                (isEnabled && (timberListHasIssues || timberOrderLengthUnder8Min))) && (
                <div className="structure-card-top-badges" aria-label="Structure badges">
                    {quantity > 1 && (
                        <span className="quantity-badge">×{quantity}</span>
                    )}
                    {isEnabled && nodeSet.clientOptional && (
                        <span
                            className="structure-client-optional-badge"
                            title="Client can opt out of this structure on the public proposal"
                        >
                            Optional
                        </span>
                    )}
                    {isEnabled && isClientExclusive && (
                        <span
                            className="structure-exclusive-group-badge"
                            title="Client can purchase only one of the structures marked Choose one"
                        >
                            Choose 1
                        </span>
                    )}
                    {isEnabled && nodeSet.excludeFromGlobalPrices && (
                        <span
                            className="structure-no-global-badge"
                            title="Does not receive a share of Global Project Costs (split among other structures)"
                        >
                            No global share
                        </span>
                    )}
                    {isEnabled && nodeSet.excludeInstall && (
                        <span
                            className="structure-exclude-install-badge"
                            title="Install and travel excluded from client pricing"
                        >
                            Install Excluded
                        </span>
                    )}
                    {isEnabled && timberListHasIssues && (
                        <span
                            className="structure-timber-issue-badge"
                            title="Open structure details to resolve pricing, dimensions, or other timber line issues"
                        >
                            <AlertTriangle size={12} aria-hidden />
                            Pricing Needed
                        </span>
                    )}
                    {isEnabled && timberOrderLengthUnder8Min && (
                        <span
                            className="structure-order-length-badge"
                            title="At least one piece has order length under 8 feet — verify with supplier rules"
                        >
                            <AlertTriangle size={12} aria-hidden />
                            8&apos; Order Length Min
                        </span>
                    )}
                </div>
            )}
            <div className="structure-card-heading">
                <h3 className="structure-card-title">{nodeSet.name}</h3>
                {isEnabled && phaseOptions && phaseOptions.length > 0 && (
                    <div className="structure-phase-field" onClick={(e) => e.stopPropagation()}>
                        <label className="structure-phase-label" htmlFor={`phase-${nodeSet.id}`}>
                            Phase
                        </label>
                        <select
                            id={`phase-${nodeSet.id}`}
                            className="structure-phase-select"
                            value={phaseId || phaseOptions[0].id}
                            disabled={phaseLocked || !onPhaseChange}
                            onChange={(e) => onPhaseChange?.(nodeSet.id, e.target.value)}
                            title={
                                phaseLocked
                                    ? 'This structure is in a committed phase — assignment is locked'
                                    : 'Assign this structure to a phase'
                            }
                        >
                            {phaseOptions.map(opt => (
                                <option key={opt.id} value={opt.id}>
                                    {opt.name}{opt.committed ? ' (committed)' : ''}
                                </option>
                            ))}
                        </select>
                    </div>
                )}
            </div>

            {!isEnabled && (
                <div className="structure-excluded-label">Excluded</div>
            )}

            {isEnabled && kitMode && availableSpecies.length > 0 ? (
                <div className="species-selection">
                    <div className="species-label">
                        <Trees size={14} />
                        <span>Species</span>
                    </div>
                    <div className="species-chips">
                        {availableSpecies.map(species => {
                            const isSelected = selectedSpecies.includes(species);
                            return (
                                <button
                                    key={species}
                                    className={`species-chip ${isSelected ? 'selected' : ''}`}
                                    onClick={(e) => handleSpeciesClick(e, species)}
                                    title={`Toggle ${species}`}
                                >
                                    {species}
                                    {isSelected && <CheckCircle size={12} />}
                                </button>
                            );
                        })}
                    </div>
                </div>
            ) : isEnabled ? (
                <>
                    {timberSpecSummary ? (
                        <div className="structure-card-timber-spec" title="Timber grade, species, and dryness">
                            {timberSpecSummary}
                        </div>
                    ) : null}
                    {!hidePrice && (
                        <div className="structure-card-price-block">
                            <div className="structure-card-total">{formatCurrency(total)}</div>
                            {projectSharePercent != null && projectSharePercent > 0 && (
                                <div className="structure-card-meta-row">
                                    <span className="structure-card-pct" title="Share of project raw total">
                                        {projectSharePercent}%
                                    </span>
                                </div>
                            )}
                        </div>
                    )}
                </>
            ) : null}
        </div>
    );
};

interface AddStructureCardProps {
    onClick: () => void;
    disabled?: boolean;
}

export const AddStructureCard: React.FC<AddStructureCardProps> = ({ onClick, disabled = false }) => {
    return (
        <div
            className={`structure-grid-card add-card ${disabled ? 'add-card-disabled' : ''}`}
            onClick={() => { if (!disabled) onClick(); }}
            title={disabled ? 'Unavailable' : undefined}
        >
            <div className="structure-card-icon">
                <Plus size={32} />
            </div>
            <span className="add-card-text">Add Structure</span>
        </div>
    );
};

export default StructureGridCard;
